// 말소 진행 (차량 › 말소 진행) — v1 components/CancelTracking.js 의 v2 (URL /main/cancel-tracking 그대로).
//   백엔드: GET /cancel-tracking/my, 소켓 cancel:step (단계 전이 시 목록 갱신 — 토스트는 Dashboard 전역 리스너)
// 장부 행(차량·단계 MicroStrip 4칸·대행사·날짜) + 행 클릭 → SlidePanel. 페이지 안 h1 없음(그룹 탭 '말소 진행').
// 패널 본문 = 말소 화면의 말소신청 상세(MalsoDetailBody — 단계·요청 서류·말소증·수거·신청 정보·신청 취소, Jin 10/29)
// + 하단 [차량 상세정보 보기]. 상세 조회가 안 되는 건(다른 사업자 건 등)은 종전 스텝퍼 + 일정으로 그린다.
// props: showToast, user
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, authFetch } from '../../utils/api';
import { getSocket } from '../../utils/socket';
import { useIsMobile } from '../../utils/useMobile';
import { canScopeUser } from '../scope';
import { T, S, FONT } from '../theme';
import { useGroupTabs, Card, CountSeg, Ledger, MicroStrip, Pill, SlidePanel, Sec, EmptyState } from '../kit';
import { StepStrip, CANCEL_STEPS } from './parts/EmptyGuide';
import { useT } from '../../i18n/I18nProvider';
import { MalsoDetailBody } from '../malso/screens/MalsoDetail';

// 표시 단계(4) — 진행중은 담당배정에, 보류는 별도 뱃지 (v1 STEPS/dispIndex 그대로)
export var STEPS = [
  { key: '대기', label: '접수대기', icon: '📥' },
  { key: '접수', label: '담당배정', icon: '🙋' },
  { key: '수거완료', label: '번호판 수거완료', icon: '📦' },
  { key: '완료', label: '말소완료', icon: '✅' }
];
export function dispIndex(status) {
  if (status === '완료') return 3;
  if (status === '수거완료') return 2;
  if (status === '접수' || status === '진행중' || status === '보류') return 1;
  return 0;
}
var SEG = [{ id: 'all', label: '전체' }, { id: 's0', label: '접수대기' }, { id: 's1', label: '담당배정' }, { id: 's2', label: '수거완료' }, { id: 's3', label: '말소완료' }, { id: 'hold', label: '보류' }];
function segOf(it) { return it.status === '보류' ? 'hold' : 's' + dispIndex(it.status); }

var tl = { display: 'grid', gridTemplateColumns: '72px 1fr', rowGap: 6, columnGap: 10, fontSize: FONT.body, color: T.body };
var tlk = { color: T.muted, fontWeight: 700, fontSize: FONT.label, alignSelf: 'center' };

// 패널 스텝퍼 — v1 :67-83 (원 36 + 연결선 3px)
function Stepper(props) {
  var cur = props.cur, isHold = props.isHold;
  return (<div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0' }}>
    {STEPS.map(function (s, i) {
      var done = i <= cur;
      var isCur = i === cur && !isHold;
      return (<React.Fragment key={s.key}>
        <div style={{ flex: '0 0 auto', textAlign: 'center', width: 78 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            background: done ? (i === 3 ? T.ok : T.navy) : T.bd, color: done ? T.card : T.faint, boxShadow: isCur ? '0 0 0 4px ' + T.accentBd : 'none' }}>{s.icon}</div>
          <div style={{ fontSize: FONT.label, marginTop: 6, fontWeight: isCur ? 800 : 600, color: done ? T.navy : T.faint }}>{s.label}</div>
        </div>
        {i < STEPS.length - 1 ? <div style={{ flex: 1, height: 3, background: i < cur ? T.navy : T.bd, marginTop: 17, borderRadius: 2 }} /> : null}
      </React.Fragment>);
    })}
  </div>);
}

// 행 안 4칸 마이크로 스트립 — 완료 칸 navy(마지막 ok), 미완료 bd, 보류는 warn
function stepStrip(it) {
  var cur = dispIndex(it.status), hold = it.status === '보류';
  return STEPS.map(function (s, i) {
    var c = i <= cur ? (hold ? T.dotWarn : (i === 3 ? T.ok : T.navy)) : T.bd;
    return { label: s.label, count: 1, color: c };
  });
}
// 최근 일정 1줄 (표) — v1 :86-91 의 항목 중 가장 뒤 단계
function latest(it) {
  if (it.status === '완료' && it.completedDate) return { t: '완료 ' + it.completedDate, c: T.ok };
  if (it.status === '보류' && it.hold_reason) return { t: '사유: ' + it.hold_reason, c: T.warn };
  if (it.picked_up_at) return { t: '수거 ' + String(it.picked_up_at).slice(0, 16), c: T.body };
  if (it.eta_at && it.status !== '완료') return { t: '도착예정 ' + it.eta_at, c: T.accentPress };
  if (it.acceptedAt) return { t: '접수 ' + it.acceptedAt, c: T.body };
  return { t: '-', c: T.faint };
}

function CancelTrackingScreen(props) {
  var t = useT();
  var isMobile = useIsMobile();
  var navigate = useNavigate();
  var tabs = useGroupTabs('cars', 'cancel-tracking');
  // 이 목록은 서버가 소속 업체 전부를 합쳐서 준다(routes/cancelAgency.js docCompanyIds) — 업체를 넘나드는
  // 계정(서류대행·관리자)에게만 행 소속 업체를 보여준다. 수출업체는 단일 업체라 컬럼이 무의미. (REVIEW P1-1)
  var multi = canScopeUser(props.user);
  var itSt = useState([]); var items = itSt[0], setItems = itSt[1];
  var ldSt = useState(true); var loading = ldSt[0], setLoading = ldSt[1];
  var segSt = useState('all'); var seg = segSt[0], setSeg = segSt[1];
  var selSt = useState(0); var selId = selSt[0], setSelId = selSt[1];

  function load() {
    authFetch(API + '/cancel-tracking/my').then(function (r) { return r.json(); })
      .then(function (d) { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(function () { setLoading(false); });
  }
  useEffect(function () { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function () {
    var sock = getSocket(); if (!sock) return undefined;
    // 토스트는 Dashboard 전역 리스너가 담당 → 여기선 목록 실시간 갱신만 (중복 토스트 방지)
    function onStep() { load(); }
    sock.on('cancel:step', onStep);
    return function () { try { sock.off('cancel:step', onStep); } catch (e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var counts = {};
  items.forEach(function (it) { var k = segOf(it); counts[k] = (counts[k] || 0) + 1; });
  var rows = seg === 'all' ? items : items.filter(function (it) { return segOf(it) === seg; });
  var sel = selId ? items.find(function (x) { return x.id === selId; }) : null;
  var selCur = sel ? dispIndex(sel.status) : 0;
  var selHold = sel ? sel.status === '보류' : false;

  return (<div className="v2-docs-tracking">
    {tabs}
    <Card title={multi ? '말소 진행 (전 업체)' : '말소 진행'} count={items.length + '건'} right={<span>의뢰하신 차량의 말소 진행 상황입니다. 단계가 바뀌면 자동으로 알려드립니다.</span>}>
      <CountSeg value={seg} onChange={setSeg} items={SEG.map(function (s) { return { id: s.id, label: s.label, count: s.id === 'all' ? items.length : (counts[s.id] || 0), color: s.id === 'hold' ? T.dotWarn : undefined }; })} />
      {loading || !rows.length ? <div>
        {/* 한 건도 없는 빈 화면에서만 다음 행동 + 흐름 설명 (X1) — 세그 필터로 비어 보이는 것은 종전 문구만 */}
        <EmptyState loaded={!loading} text="진행 중인 말소 건이 없습니다"
          action={!loading && !items.length ? { label: '차량 목록에서 말소 대행 신청 →', onClick: function () { navigate('/main/car-list'); } } : null} />
        {!loading && !items.length ? <StepStrip label="말소 대행 진행 4단계" steps={CANCEL_STEPS} isMobile={isMobile} /> : null}
      </div> : <Ledger minWidth={multi ? 1080 : 960}
        foot={seg === 'all' ? null : <span>표시 <b>{rows.length}</b>건 <span className="mute">/ 전체 {items.length}건</span></span>}>
        <thead><tr>{multi ? <th style={{ width: 130 }}>업체</th> : null}<th>차량번호</th><th>차종</th><th>말소 유형</th><th>대행사</th><th style={{ width: 150 }}>단계</th><th>현재</th><th>요청일</th><th>최근</th></tr></thead>
        <tbody>
          {rows.map(function (it) {
            var cur = dispIndex(it.status), hold = it.status === '보류', lt = latest(it);
            return (<tr key={it.id} className={selId === it.id ? 'sel' : ''} onClick={function () { setSelId(it.id); }}>
              {multi ? <td style={{ color: T.sub }}>{it.companyName || '-'}</td> : null}
              <td className="k">{it.carNo || '-'}</td>
              <td className="mute">{it.carType || ''}</td>
              <td className="mute">{it.cancelType || ''}</td>
              <td>{it.agencyName || '-'}</td>
              <td><MicroStrip segments={stepStrip(it)} total={4} /></td>
              <td>{hold ? <Pill tone="warn">⏸ 보류</Pill> : <Pill tone={cur === 3 ? 'ok' : 'blue'}>{STEPS[cur].label}</Pill>}</td>
              <td className="mute">{it.requestDate || '-'}</td>
              <td style={{ color: lt.c }}>{lt.t}</td>
            </tr>);
          })}
        </tbody>
      </Ledger>}
    </Card>

    <SlidePanel open={!!sel} width={520} isMobile={isMobile} title={t('cancelHistoryDetailTitle')}
      onClose={function () { setSelId(0); }}
      footer={sel && sel.carId ? <button type="button" data-action="open-car" style={Object.assign({}, S.btnNavy, { flex: 1, justifyContent: 'center' })}
        onClick={function () { navigate('/main/car-list', { state: { search: sel.carNo || '', allPeriod: true, openCarId: sel.carId } }); }}>{t('cancelDetailGoToCar')}</button> : null}>
      {sel ? <MalsoDetailBody key={sel.id} id={sel.id} row={sel} user={props.user} showToast={props.showToast} flush
        onChanged={load} onDeleted={function () { setSelId(0); }}
        fallback={<div>
          <div style={{ fontSize: FONT.title, fontWeight: 800, color: T.navy, marginTop: 8 }}>{sel.carNo || '-'}</div>
          <Sec label="진행 단계" />
          <Stepper cur={selCur} isHold={selHold} />
          <Sec label="일정" />
          <div style={tl}>
            {sel.requestDate ? <React.Fragment><span style={tlk}>요청일</span><span>{sel.requestDate}</span></React.Fragment> : null}
            {sel.acceptedAt ? <React.Fragment><span style={tlk}>접수</span><span>{sel.acceptedAt}</span></React.Fragment> : null}
            {sel.eta_at && sel.status !== '완료' ? <React.Fragment><span style={tlk}>도착예정</span><span style={{ color: T.accentPress }}>{sel.eta_at}</span></React.Fragment> : null}
            {sel.picked_up_at ? <React.Fragment><span style={tlk}>수거</span><span>{String(sel.picked_up_at).slice(0, 16)}</span></React.Fragment> : null}
            {sel.completedDate ? <React.Fragment><span style={tlk}>완료</span><b style={{ color: T.ok }}>{sel.completedDate}</b></React.Fragment> : null}
            {selHold && sel.hold_reason ? <React.Fragment><span style={tlk}>사유</span><span style={{ color: T.warn }}>{sel.hold_reason}</span></React.Fragment> : null}
          </div>
        </div>} /> : null}
    </SlidePanel>
  </div>);
}

export default CancelTrackingScreen;
